import {cast, Instance, types} from "mobx-state-tree";
import {getGqlModelBase} from "../gqlModel/GqlModelBase";
import {TaskReport} from "./gql/serverTypes";
import type {TaskReportGqlProvider} from "./TaskReportGqlProvider";
import type {IDataClientService} from "../../platform/services/data/DataClientService";
import type {IAuthService} from "../../platform/services/auth/AuthService";

export interface TaskReportFileMeta {
    path: string,
    thumbPath: string,
    uploadedAt: string,
    userId: string,
}

export type TaskReportModelType = Instance<typeof TaskReportModel>;

export interface TaskReportModelEnv {
    getDataClientService: () => IDataClientService;
    authService: IAuthService;
    queryProvider: TaskReportGqlProvider;
    getTaskListUnitMeta: (taskListUnitId: string) => {
        timezone?: string | null;
        logicalDayShift?: number | null;
    } | undefined;
}

export const TaskReportModel = getGqlModelBase<TaskReport>()
    .named("TaskReportModel")
    .props({
        id: types.optional(types.identifier, ''),
        taskId: types.string,
        taskListUnitId: types.string,
        completedAt: types.string,
        comment: types.optional(types.maybeNull(types.string), ''),
        images: types.optional(types.maybeNull(types.array(types.frozen<TaskReportFileMeta>())), []),
        commentImages: types.optional(types.maybeNull(types.array(types.frozen<TaskReportFileMeta>())), []),
        completedBy: types.optional(types.maybeNull(types.array(types.string)), []),
        status: types.optional(types.string, 'completed'),
        version: types.optional(types.maybeNull(types.number), 0),
        createdAt: types.optional(types.string, ''),
        updatedAt: types.optional(types.string, ''),
    })
    .views(self => ({
        get hasError() {
            return false;
        }
    }))
    .actions(self => ({
        proceedSaveResults(obj: TaskReport) { // @ts-ignore
            self.lastSavedObject = obj!;
            self.version = obj.version;
        },
        setStatus(status: string) {
            self.status = status;
            self.hasChanges = true;
        },
        setComment(comment: string) {
            self.comment = comment;
            self.hasChanges = true;
        },
        setImages(imagePaths: TaskReportFileMeta[]) {
            if (self.images === null){
                self.images = cast(imagePaths);
            } else {
                self.images.replace(imagePaths);
            }
            self.hasChanges = true;
        },
        setCommentImages(imagePaths: TaskReportFileMeta[]) {
            if (self.commentImages === null){
                self.commentImages = cast(imagePaths);
            } else {
                self.commentImages.replace(imagePaths);
            }
            self.hasChanges = true;
        },
        setCompletedBy(userIds: string[] | null) {
            if (userIds === null) {
                self.completedBy = null;
            } else if (self.completedBy) {
                self.completedBy.replace(userIds);
            } else {
                self.completedBy = cast(userIds);
            }
            self.hasChanges = true;
        },
    }));
